import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DynamicTestSection from './TestingComponents/DynamicTestSection';
import { testingData } from './TestingComponents/testingData';

const useStyles = makeStyles(() => ({
  pageHeader: {
    // marginBottom: '48px',
    // paddingBottom: '24px',
    // borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '32px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '8px',
    letterSpacing: '-0.025em',
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    fontWeight: 400,
    lineHeight: 1.5,
    marginBottom: '32px',
  },
  instructionsSection: {
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '32px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)',
      borderRadius: '12px 12px 0 0',
    },
  },
  instructionsTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
    letterSpacing: '-0.01em',
  },
  instructionsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& li': {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '12px',
      fontSize: '14px',
      color: '#4b5563',
      lineHeight: 1.5,
      '&::before': {
        content: '"•"',
        color: '#9ca3af',
        fontWeight: 'bold',
        width: '16px',
        flexShrink: 0,
        marginTop: '1px',
      },
    },
  },
  modeSelector: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px',
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  modeTab: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      color: '#374151',
    },
  },
  activeModeTab: {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(30, 41, 59, 0.2)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: '#ffffff',
    },
  },
  modeDescription: {
    textAlign: 'center',
    marginBottom: '32px',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderRadius: '8px',
    border: '1px solid #bfdbfe',
    '& h4': {
      margin: '0 0 8px 0',
      color: '#1e40af',
      fontSize: '16px',
      fontWeight: 600,
    },
    '& p': {
      margin: 0,
      color: '#3730a3',
      fontSize: '14px',
      lineHeight: 1.5,
    },
  },
  sectionContainer: {
    '&.hidden': {
      display: 'none',
    },
  },
  instructionsToggle: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid #d1d5db',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: '#6b7280',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: '#ffffff',
      borderColor: '#9ca3af',
      color: '#374151',
      transform: 'scale(1.05)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&.active': {
      backgroundColor: '#3b82f6',
      borderColor: '#3b82f6',
      color: '#ffffff',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
    },
  },
  instructionsCollapsed: {
    display: 'none',
  },
  instructionsExpanded: {
    marginBottom: '24px',
    animation: '$slideDown 0.3s ease-out',
  },
  '@keyframes slideDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)',
      maxHeight: 0,
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
      maxHeight: '500px',
    },
  },
  testingControls: {
    marginBottom: '40px',
    padding: '20px 24px',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    boxShadow: '0 4px 20px rgba(30, 41, 59, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(148, 163, 184, 0.3) 50%, transparent 100%)',
    },
  },
  resetButton: {
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: '#dc2626',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(220, 38, 38, 0.3)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  progressIndicator: {
    fontWeight: 600,
    color: '#e2e8f0',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    '&::before': {
      content: '"Progress:"',
      color: '#cbd5e1',
      fontWeight: 400,
    },
  },
  exportButton: {
    backgroundColor: 'rgba(5, 150, 105, 0.9)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      backgroundColor: '#047857',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(4, 120, 87, 0.3)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  controlsGroup: {
    display: 'flex',
    gap: '12px',
  },
  testerNameField: {
    padding: '10px 14px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '200px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    color: '#1f2937',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), 0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    '&::placeholder': {
      color: '#6b7280',
    },
  },
  testerNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    '& label': {
      fontSize: '14px',
      color: '#e2e8f0',
      fontWeight: 500,
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    },
  },
  sectionHeader: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#111827',
    marginTop: '48px',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
    paddingBottom: '8px',
    borderBottom: '2px solid transparent',
    backgroundImage: 'linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)',
    backgroundSize: '100% 2px',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom',
  },
}));

// Default test sets for different modes
const getDefaultTestSets = () => ({
  feature: ['user-list', 'user-edit', 'documentation-system'], // Current feature development
  release: ['user-list', 'user-edit', 'documentation-system'], // Documentation system added to release testing
  smoke: [ // Permanent core functionality tests
    'dashboard',
    'patient-list',
    'patient-edit',
    'claims-list',
    'claims-edit',
    'user-list',
    'user-edit',
    'documentation-system'
  ]
});

const TestingChecklist = () => {
  const classes = useStyles();
  const [testerName, setTesterName] = useState('');
  const [activeMode, setActiveMode] = useState('feature'); // 'feature', 'release', 'smoke'
  const [currentTestSets, setCurrentTestSets] = useState(getDefaultTestSets());
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Load tester name from localStorage
    const savedName = localStorage.getItem('qhs-tester-name');
    if (savedName) {
      setTesterName(savedName);
    }

    // Load test sets from localStorage
    const savedTestSets = localStorage.getItem('qhs-test-sets');
    if (savedTestSets) {
      const parsed = JSON.parse(savedTestSets);
      // Check if documentation-system exists in saved data, if not - add it
      const defaultSets = getDefaultTestSets();
      const updatedSets = { ...parsed };

      Object.keys(defaultSets).forEach(mode => {
        if (!updatedSets[mode]) {
          updatedSets[mode] = defaultSets[mode];
        } else {
          // Add missing test sections if they're not already there
          defaultSets[mode].forEach(sectionId => {
            if (!updatedSets[mode].includes(sectionId)) {
              updatedSets[mode] = [...updatedSets[mode], sectionId];
            }
          });
        }
      });

      setCurrentTestSets(updatedSets);
      localStorage.setItem('qhs-test-sets', JSON.stringify(updatedSets));
      console.log('Updated test sets:', updatedSets);
    }

    // Set global current mode for child components
    window.currentTestingMode = activeMode;
  }, []);

  useEffect(() => {
    // Update global mode when it changes
    window.currentTestingMode = activeMode;

    // Force reload of test states when mode changes
    setTimeout(() => {
      window.updateProgress();
      window.updateAllSectionProgress();

      // Trigger test reload for all components
      window.dispatchEvent(new CustomEvent('modeChanged', { detail: { mode: activeMode } }));
    }, 100);
  }, [activeMode]);

  const handleTesterNameChange = (e) => {
    const name = e.target.value;
    setTesterName(name);
    localStorage.setItem('qhs-tester-name', name);
  };

  const getModeTitle = (mode) => {
    switch (mode) {
      case 'feature':
        return 'Feature Testing';
      case 'release':
        return 'Release Testing';
      case 'smoke':
        return 'Smoke Testing';
      default:
        return 'Testing';
    }
  };

  const getModeDescription = (mode) => {
    const testCount = currentTestSets[mode] ? currentTestSets[mode].length : 0;
    switch (mode) {
      case 'feature':
        return {
          title: 'Feature Testing',
          description: `Tests for current build/feature only (${testCount} sections). Add tests as you develop new features.`
        };
      case 'release':
        return {
          title: 'Release Testing',
          description: `Accumulated tests for current release (${testCount} sections). Auto-populated from Feature Testing.`
        };
      case 'smoke':
        return {
          title: 'Smoke Testing',
          description: `Core functionality tests (${testCount} sections). Permanent set for basic system verification.`
        };
      default:
        return { title: 'Testing', description: '' };
    }
  };


  const isAvailableSection = (sectionId) => {
    return currentTestSets[activeMode] && currentTestSets[activeMode].includes(sectionId);
  };

  const getStorageKey = (baseKey) => {
    return `${baseKey}-${activeMode}`;
  };

  // Update global test sets for child components
  useEffect(() => {
    window.currentTestSets = currentTestSets;
  }, [currentTestSets]);

  const generatePDF = () => {
    const testItems = document.querySelectorAll('.test-item');

    if (testItems.length === 0) {
      alert('No test items found. Please wait for the page to load completely.');
      return;
    }

    // Create new PDF document
    const doc = new jsPDF();
    const today = new Date();
    const dateStr = today.toLocaleDateString();
    const modeTitle = getModeTitle(activeMode);

    // Add elegant header with modern typography
    doc.setFontSize(24);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(31, 41, 55); // #1f2937
    doc.text(`${modeTitle} Results Report`, 20, 25);

    // Subtle line under title
    doc.setDrawColor(229, 231, 235); // #e5e7eb
    doc.setLineWidth(0.5);
    doc.line(20, 30, 190, 30);

    // Metadata in clean, minimal style
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128); // #6b7280
    doc.text(`Generated on ${dateStr}`, 20, 40);
    doc.text(`Tested by ${testerName || 'Not specified'}`, 20, 45);
    // We'll update this after calculating stats

    const tableData = [];
    const sectionData = {};

    // First pass: organize data by sections and identify tested sections
    testItems.forEach(item => {
      const testId = item.getAttribute('data-test');
      const sectionId = item.getAttribute('data-section');
      const status = item.getAttribute('data-status') || '';

      // Get description from the test item
      const descriptionElement = item.querySelector('.testDescription');
      const description = descriptionElement?.textContent?.trim() || testId || 'Unknown Test';

      // Map our status to QA format
      let qaStatus = 'Not Tested';
      if (status === 'passed') {
        qaStatus = 'PASSED';
      } else if (status === 'failed') {
        qaStatus = 'FAILED';
      }

      const sectionName = sectionId ? sectionId.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Section';

      // Initialize section if not exists
      if (!sectionData[sectionName]) {
        sectionData[sectionName] = [];
      }

      // Get comment for this test
      const commentField = document.querySelector(`[data-test-comment="${testId}"]`);
      const comment = commentField ? commentField.value : '';

      // Add test to section data
      sectionData[sectionName].push({
        description,
        status: qaStatus,
        comment,
        hasStatus: status && status.trim() !== '' // Track if test has been conducted
      });
    });

    // Second pass: build table data only for sections with tested items and calculate stats
    const stats = {
      passed: 0,
      failed: 0,
      notTested: 0
    };

    Object.keys(sectionData).forEach(sectionName => {
      const sectionTests = sectionData[sectionName];
      const testedItems = sectionTests.filter(test => test.hasStatus);

      // Only add section if it has at least one tested item
      if (testedItems.length > 0) {
        // Add section header row
        tableData.push([
          { content: sectionName, colSpan: 3, styles: { fillColor: [200, 200, 200], fontStyle: 'bold' } }
        ]);

        // Add all tests from this section and count stats
        sectionTests.forEach(test => {
          tableData.push([
            test.description,
            test.status,
            test.comment
          ]);

          // Count statistics only for tests that will appear in the report
          if (test.status === 'PASSED') {
            stats.passed++;
          } else if (test.status === 'FAILED') {
            stats.failed++;
          } else {
            stats.notTested++;
          }
        });
      }
    });

    // Calculate total tests in report and add header info
    const totalTestsInReport = stats.passed + stats.failed + stats.notTested;
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128); // #6b7280
    doc.text(`Total Tests: ${totalTestsInReport}`, 20, 50);

    // Add elegant statistics with modern styling
    doc.setFontSize(9);
    doc.setTextColor(75, 85, 99); // #4b5563

    // Statistics with refined spacing and typography
    const passedText = `${stats.passed} Passed`;
    const failedText = `${stats.failed} Failed`;
    const notTestedText = `${stats.notTested} Not Tested`;

    doc.text(passedText, 20, 58);
    doc.text('•', 20 + doc.getTextWidth(passedText) + 5, 58);
    doc.text(failedText, 20 + doc.getTextWidth(passedText) + 12, 58);
    doc.text('•', 20 + doc.getTextWidth(passedText + failedText) + 17, 58);
    doc.text(notTestedText, 20 + doc.getTextWidth(passedText + failedText) + 24, 58);

    // Add elegant, minimal table
    autoTable(doc, {
      startY: 68,
      head: [['Test Description', 'Status', 'Comments']],
      body: tableData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
        font: 'helvetica',
        textColor: [31, 41, 55], // #1f2937
        lineColor: [229, 231, 235], // #e5e7eb
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [249, 250, 251], // #f9fafb - Very light gray
        textColor: [75, 85, 99], // #4b5563 - Darker gray
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
      },
      alternateRowStyles: {
        fillColor: [254, 254, 255], // Very subtle alternating rows
      },
      columnStyles: {
        0: {
          cellWidth: 100,
          fontStyle: 'normal',
          textColor: [31, 41, 55], // #1f2937
        },
        1: {
          cellWidth: 35,
          halign: 'center',
          fontStyle: 'bold',
          fontSize: 8,
        },
        2: {
          cellWidth: 50,
          fontSize: 8,
          textColor: [75, 85, 99], // #4b5563
        },
      },
      didParseCell: function(data) {
        // Section headers with elegant styling
        if (data.cell.colSpan === 3) {
          data.cell.styles.fillColor = [31, 41, 55]; // #1f2937
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;
          data.cell.styles.cellPadding = { top: 6, right: 6, bottom: 6, left: 6 };
        }

        // Elegant status cell styling
        if (data.column.index === 1 && data.cell.text[0] && data.cell.colSpan !== 3) {
          const status = data.cell.text[0];
          if (status === 'PASSED') {
            data.cell.styles.fillColor = [240, 253, 244]; // #f0fdf4 - Very light green
            data.cell.styles.textColor = [21, 128, 61]; // #15803d - Green
          } else if (status === 'FAILED') {
            data.cell.styles.fillColor = [254, 242, 242]; // #fef2f2 - Very light red
            data.cell.styles.textColor = [185, 28, 28]; // #b91c1c - Red
          } else if (status === 'Not Tested') {
            data.cell.styles.fillColor = [249, 250, 251]; // #f9fafb - Light gray
            data.cell.styles.textColor = [156, 163, 175]; // #9ca3af - Gray
          }
        }
      }
    });

    // Save the PDF
    const filename = `QHS-${activeMode.charAt(0).toUpperCase() + activeMode.slice(1)}-Testing-${today.toISOString().split('T')[0]}.pdf`;
    doc.save(filename);

    console.log('PDF Generated:', testItems.length, 'tests included');
  };

  useEffect(() => {
    // Initialize functionality after component mounts
    const initializeChecklist = () => {
      // Toggle section function
      window.toggleSection = (sectionId) => {
        const content = document.getElementById(`${sectionId}-content`);
        const icon = document.getElementById(`${sectionId}-icon`);

        if (content && icon) {
          const isExpanded = content.classList.contains('expanded');

          if (isExpanded) {
            content.classList.remove('expanded');
            icon.classList.remove('expanded');
          } else {
            content.classList.add('expanded');
            icon.classList.add('expanded');
          }
        }
      };

      // Reset all tests function
      window.resetAllTests = () => {
        if (window.confirm('Are you sure you want to reset all test progress and comments?')) {
          const testItems = document.querySelectorAll('.test-item');
          testItems.forEach(item => {
            item.classList.remove('test-passed', 'test-failed');
            item.removeAttribute('data-status');

            // Clear comment fields
            const testId = item.dataset.test;
            const commentField = document.querySelector(`[data-test-comment="${testId}"]`);
            if (commentField) {
              commentField.value = '';
            }
          });
          localStorage.removeItem(getStorageKey('qhs-testing-progress'));
          window.updateProgress();
          window.updateAllSectionProgress();

          // Trigger React state updates for comments
          window.dispatchEvent(new CustomEvent('resetTests'));
        }
      };

      // Reset section tests function
      window.resetSectionTests = (sectionId) => {
        if (window.confirm(`Are you sure you want to reset all tests in this section?`)) {
          const testItems = document.querySelectorAll(`.test-item[data-section="${sectionId}"]`);
          testItems.forEach(item => {
            item.classList.remove('test-passed', 'test-failed', 'test-skipped');
            item.removeAttribute('data-status');
          });
          const saved = localStorage.getItem(getStorageKey('qhs-testing-progress'));
          if (saved) {
            const states = JSON.parse(saved);
            testItems.forEach(item => {
              delete states[item.dataset.test];
            });
            localStorage.setItem(getStorageKey('qhs-testing-progress'), JSON.stringify(states));
          }
          window.updateProgress();
          window.updateSectionProgress(sectionId);
        }
      };

      // Update overall progress
      window.updateProgress = () => {
        const testItems = document.querySelectorAll('.test-item');
        const completed = Array.from(testItems).filter(item => {
          const status = item.getAttribute('data-status');
          return status && status.trim() !== '' && status !== 'skipped';
        });
        const progressText = document.getElementById('progress-text');
        if (progressText) {
          progressText.textContent = `${completed.length}/${testItems.length}`;
        }
      };

      // Update section progress
      window.updateSectionProgress = (sectionId) => {
        const sectionItems = document.querySelectorAll(`.test-item[data-section="${sectionId}"]`);
        const sectionCompleted = Array.from(sectionItems).filter(item => {
          const status = item.getAttribute('data-status');
          return status && status.trim() !== '' && status !== 'skipped';
        });
        const progressFill = document.getElementById(`${sectionId}-progress`);
        const progressText = document.getElementById(`${sectionId}-text`);
        const completionIcon = document.getElementById(`${sectionId}-done`);

        if (sectionItems.length > 0) {
          const percentage = (sectionCompleted.length / sectionItems.length) * 100;

          if (progressFill) {
            progressFill.style.width = `${percentage}%`;
          }

          if (progressText) {
            progressText.textContent = `${sectionCompleted.length}/${sectionItems.length}`;
          }

          if (completionIcon) {
            if (percentage === 100) {
              completionIcon.classList.add('completed');
              progressText.style.display = 'none';
            } else {
              completionIcon.classList.remove('completed');
              progressText.style.display = 'block';
            }
          }
        }
      };

      // Update all section progress
      window.updateAllSectionProgress = () => {
        const sections = ['dashboard', 'patient-list', 'patient-edit', 'claims-list', 'claims-edit', 'user-list', 'user-edit', 'documentation-system'];
        sections.forEach(sectionId => {
          window.updateSectionProgress(sectionId);
        });
      };

      // Load saved test states
      const loadTestStates = () => {
        // This is now handled by individual TestItem components in their useEffect
        // Just trigger progress update
        setTimeout(() => {
          window.updateProgress();
          window.updateAllSectionProgress();
        }, 200);
      };

      // Save test states
      window.saveTestStates = () => {
        const testItems = document.querySelectorAll('.test-item');
        const states = {};
        testItems.forEach(item => {
          const testId = item.dataset.test;
          const status = item.getAttribute('data-status');
          const commentField = document.querySelector(`[data-test-comment="${testId}"]`);
          const comment = commentField ? commentField.value : '';

          if (status || comment) {
            states[testId] = {
              status: status || '',
              comment: comment
            };
          }
        });
        localStorage.setItem(getStorageKey('qhs-testing-progress'), JSON.stringify(states));
        window.updateProgress();
        window.updateAllSectionProgress();
      };

      // Initialize
      setTimeout(() => {
        loadTestStates();

        // Event listeners are now handled in TestItem components

        // Initialize section progress
        window.updateAllSectionProgress();
      }, 100);
    };

    initializeChecklist();

    // Cleanup function
    return () => {
      delete window.toggleSection;
      delete window.resetAllTests;
      delete window.resetSectionTests;
      delete window.updateProgress;
      delete window.updateSectionProgress;
      delete window.updateAllSectionProgress;
    };
  }, []);

  return (
    <div>
      <div className={classes.pageHeader} style={{ position: 'relative' }}>
        {/* Instructions Toggle Button */}
        <button
          className={`${classes.instructionsToggle} ${showInstructions ? 'active' : ''}`}
          onClick={() => setShowInstructions(!showInstructions)}
          title={showInstructions ? 'Hide instructions' : 'Show instructions'}
        >
          ?
        </button>

        {/* Mode Selector */}
        <div className={classes.modeSelector}>
          <button
            className={`${classes.modeTab} ${activeMode === 'feature' ? classes.activeModeTab : ''}`}
            onClick={() => setActiveMode('feature')}
          >
            Feature Testing
          </button>
          <button
            className={`${classes.modeTab} ${activeMode === 'release' ? classes.activeModeTab : ''}`}
            onClick={() => setActiveMode('release')}
          >
            Release Testing
          </button>
          <button
            className={`${classes.modeTab} ${activeMode === 'smoke' ? classes.activeModeTab : ''}`}
            onClick={() => setActiveMode('smoke')}
          >
            Smoke Testing
          </button>
        </div>

        {/* Mode Description */}
        <div className={classes.modeDescription}>
          <h4>{getModeDescription(activeMode).title}</h4>
          <p>{getModeDescription(activeMode).description}</p>
        </div>


        {/* Collapsible Instructions */}
        <div className={showInstructions ? classes.instructionsExpanded : classes.instructionsCollapsed}>
          <div className={classes.instructionsSection}>
            <h3 className={classes.instructionsTitle}>How to Use This Checklist</h3>
            <ul className={classes.instructionsList}>
              <li>Select testing mode:
                <strong>Feature</strong>
                (current development),
                <strong>Release</strong>
                (accumulated tests), or
                <strong>Smoke</strong>
                (core functionality)
              </li>
              <li>Click <strong>Pass</strong> or <strong>Fail</strong> buttons for each test</li>
              <li>Test status is automatically saved per mode in your browser</li>
              <li>Click the comment icon to add detailed notes for any test</li>
              <li>Use <strong>"Export PDF Report"</strong> to generate mode-specific testing reports</li>
              <li>Use <strong>"Reset All"</strong> to clear all test statuses for current mode</li>
            </ul>
          </div>
        </div>
      </div>

      <div className={classes.testingControls}>
        <div className={classes.controlsGroup}>
          <div className={classes.testerNameContainer}>
            <label htmlFor="tester-name">Tested by:</label>
            <input
              id="tester-name"
              type="text"
              className={classes.testerNameField}
              value={testerName}
              onChange={handleTesterNameChange}
              placeholder="Enter your name"
            />
          </div>
          <button onClick={() => window.resetAllTests()} className={classes.resetButton}>
            Reset All Tests
          </button>
          <button
            onClick={generatePDF}
            className={classes.exportButton}
          >
            Export PDF Report
          </button>
        </div>
        <div className={classes.progressIndicator}>
          <span id="progress-text">0/0</span>
        </div>
      </div>

      {/* Dynamic Test Sections */}
      {Object.entries(testingData).map(([sectionKey, sectionData]) => {
        if (!isAvailableSection(sectionKey)) {
          return null;
        }

        return (
          <DynamicTestSection key={sectionKey} sectionData={sectionData} />
        );
      })}

      {/* Show message if no tests available in current mode */}
      {currentTestSets[activeMode] && currentTestSets[activeMode].length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          marginTop: '24px'
        }}>
          <h3 style={{ color: '#6b7280', marginBottom: '8px' }}>No Tests Available</h3>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>
            {activeMode === 'feature' && 'Contact the development team to configure test sections for your current feature.'}
            {activeMode === 'release' && 'Contact the development team to configure release testing sections.'}
            {activeMode === 'smoke' && 'Smoke tests should be automatically available. Contact the development team if you see this message.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default TestingChecklist;
